<script lang="ts">
  // Die Aktivitaetsleiste (docs/shell.md).
  //
  // Der immer sichtbare Einstieg: Startseite, die fuenf Sektionen aus
  // nav-sections.ts, unten Meldungen und Einstellungen.
  //
  // Ein Klick auf eine Sektion SCHALTET DIE SEITENLEISTE UM, er oeffnet
  // keinen Tab -- so wie in VS Code. Eine Uebersichtsseite gibt es am
  // Schreibtisch nicht mehr (02.10.2026), der Baum IST die Uebersicht.
  // Wuerde jeder Klick hier einen Tab
  // aufmachen, waere die Leiste eine Tab-Schleuder: fuenf Klicks zum
  // Suchen, fuenf Tabs zum Wieder-Schliessen.

  import Icon from "../Icon.svelte";
  import { t } from "../i18n.svelte";
  import { getAlertsCount, getAlertsTopSeverity } from "../alerts-store.svelte";
  import { SEVERITY_TEXT } from "../meldungs-logik";
  import { istDunkel, schalteHellDunkel, umschaltZiel } from "../theme.svelte";
  import { navSections } from "../nav-sections";
  import { ordne, verschiebe } from "../anordnung";
  import { sortierModusHalten } from "../anordnung.svelte";
  import { pressReorder } from "../press-reorder";
  import { laeuftIm } from "../laeuft.svelte";
  import { bereichReihe, seitenleisteOffen, setzeBereichReihe, sichtbareSektion, zeigeSektion } from "./chrome.svelte";
  import { offenePunkte } from "./tree.svelte";
  import { oeffnePfad } from "./route-sync.svelte";
  import { aktiverTab } from "./tabs.svelte";

  // Bereiche verschieben (Tim 19.10.2026: „Die Elemente in der Seitenleiste
  // müssen noch verschiebbar sein"): Symbol gedrückt halten öffnet den
  // Modus, gezogen wird live -- wie überall (press-reorder.ts). Daneben
  // drücken oder Escape beendet ihn. Gemerkt je Gerät (chrome.svelte.ts).
  const bereiche = $derived(ordne(bereichReihe(), navSections));
  let ordnen = $state(false);
  $effect(() => {
    if (!ordnen) return;
    return sortierModusHalten(".activity", () => (ordnen = false));
  });
  function verschiebeBereich(von: string, nach: string): void {
    const neu = verschiebe(bereichReihe(), bereiche.map((b) => b.id), von, nach);
    if (neu) setzeBereichReihe(neu);
  }

  // onpalette oeffnet die Befehlspalette. Die Suche sass frueher als
  // Feld in der oberen Leiste; die ist weggefallen, damit oben
  // Arbeitsflaeche frei wird (Tim 18.08.). Hier kostet sie keine
  // Zeile, ist immer sichtbar und gilt fuer das ganze Fenster -- bei
  // geteilter Ansicht waere ein Feld in EINER Tab-Leiste willkuerlich.
  let { onpalette }: { onpalette: () => void } = $props();

  const tab = $derived(aktiverTab());
  const aufStartseite = $derived(
    tab?.typ === "page" && String(tab.params.path ?? "") === "/",
  );
  const aufEinstellungen = $derived(
    tab?.typ === "page" && String(tab.params.path ?? "").startsWith("/einstellungen"),
  );
  // Startseite und Einstellungen gehoeren zu keinem Bereich. Steht der
  // aktive Tab dort, ist SEIN Symbol markiert (Haus, Zahnrad) und kein
  // Bereich -- sonst blieb der zuletzt gezeigte Bereich markiert, obwohl
  // man gar nicht dort war (Tim 04.10.2026: "Wenn Einstellungen geöffnet
  // sind, dann ist links noch die letzte Kategorie markiert. Das passt
  // nicht."). Welchen Baum die Seitenleiste zeigt, sagt ihr eigener Kopf.
  const ohneBereich = $derived(aufStartseite || aufEinstellungen);
  // Sonst gilt: markiert ist, was die Seitenleiste ZEIGT -- die Leiste
  // ist eine Ansichts-Umschaltung, und sie soll sagen, welche Ansicht
  // das ist.
  const sektion = $derived(sichtbareSektion());
  const leisteOffen = $derived(seitenleisteOffen());
  const meldungen = $derived(getAlertsCount());
  // Die Farbe des Dreiecks folgt der DRINGENDSTEN offenen Stufe --
  // gleiche Zuordnung wie die Pille auf der Meldungs-Seite
  // (problem/warning/info -> danger/warning/info). Vorher war das
  // Signal fest rot und behauptete einen Fehler, waehrend die einzige
  // offene Sache eine Warnung war (Tim 28.08.2026).
  const stufe = $derived(getAlertsTopSeverity());

  const dunkel = $derived(istDunkel());
  const themeTip = $derived(
    (dunkel
      ? t("shell.theme_hell", "Auf Hell umschalten")
      : t("shell.theme_dunkel", "Auf Dunkel umschalten")) +
      (umschaltZiel() === "auto" ? " — " + t("shell.theme_auto", "folgt dann wieder dem System") : ""),
  );

  // Die Farbe allein darf die Aussage nicht tragen: Beschriftung und
  // Titel sagen dasselbe in Worten. Die Stufen-Namen kommen aus der
  // gemeinsamen Tabelle, nicht aus einer zweiten Liste hier.
  const melderText = $derived.by(() => {
    const basis = t("shell.alerts", "Meldungen");
    if (meldungen === 0 || !stufe) return basis;
    const s = SEVERITY_TEXT[stufe];
    return `${basis} — ${meldungen} ${t("shell.alerts_open", "offen")}, ${t(s.key, s.fallback)}`;
  });
  // Offene Punkte je Sektion -- dieselbe Summe, die der Baum unten
  // zeigt. So sieht man schon an der Leiste, WO etwas wartet, ohne
  // eine Sektion nach der anderen aufzuklappen (Tim 15.08.).
  const punkte = $derived(
    Object.fromEntries(navSections.map((s) => [s.id, offenePunkte(s.id)])),
  );
  // Läuft in einem Bereich gerade etwas (Drucker druckt), trägt sein
  // Symbol einen kleinen Punkt -- so findet man es, ohne den Bereich
  // aufzuklappen. Gelb, wenn es auf einen Handgriff wartet.
  const laeuftJe = $derived(
    Object.fromEntries(navSections.map((s) => [s.id, laeuftIm(s.id)])),
  );

</script>

<nav class="activity flex flex-col items-center" class:ordnen aria-label={t("shell.sections", "Bereiche")}>
  <!-- Die Startseite. In VS Code ist das der Willkommens-Tab: immer
       erreichbar, aber ein Tab wie jeder andere. -->
  <button
    type="button"
    class="act"
    class:aktiv={aufStartseite}
    title={t("shell.start", "Startseite")}
    aria-label={t("shell.start", "Startseite")}
    aria-current={aufStartseite}
    onclick={() => oeffnePfad("/")}
  >
    <Icon name="home" size={21} />
  </button>

  <button
    type="button"
    class="act"
    title={t("shell.search", "Suchen und springen") + " (Strg P)"}
    aria-label={t("shell.search", "Suchen und springen")}
    onclick={onpalette}
  >
    <Icon name="search" size={21} />
  </button>

  <div class="trenner"></div>

  {#each bereiche as s (s.id)}
    <button
      type="button"
      class="act"
      use:pressReorder={{
        group: "leiste",
        id: s.id,
        armed: ordnen,
        onActivate: () => (ordnen = true),
        onReorder: (_g, von, nach) => verschiebeBereich(von, nach),
        liftClass: "anordnung-zieht",
      }}
      class:aktiv={sektion === s.id && leisteOffen && !ohneBereich}
      data-bereich={s.id}
      title={s.label}
      aria-label={s.label}
      aria-pressed={sektion === s.id && leisteOffen}
      onclick={() => zeigeSektion(s.id)}
    >
      <Icon name={s.icon} size={21} />
      {#if laeuftJe[s.id].length}
        <span class="lauf-punkt" class:warn={laeuftJe[s.id].some((l) => l.ton === "warn")} title={laeuftJe[s.id].map((l) => l.titel).join("\n")}></span>
      {/if}
      {#if punkte[s.id].punkte > 0}
        <span class="badge zaehl-marke" class:schwer={punkte[s.id].ton === "bad"}>
          {punkte[s.id].punkte > 9 ? "9+" : punkte[s.id].punkte}
        </span>
      {/if}
    </button>
  {/each}

  <div class="flex-1"></div>

  <!-- Das DREIECK SELBST traegt die Stufe (Tim 28.08.): kein Punkt, kein
       Zaehler, sondern ein Symbol, das faerbt. Die Vorgeschichte steckt
       da mit drin -- erst eine Zahl (lud dazu ein, sie runterzuzaehlen
       statt zu lesen), dann ein Punkt daneben (ein Anhaengsel mehr),
       jetzt das Zeichen, das ohnehin schon da steht.
       Was nie mitzaehlt: selbst erledigte Meldungen (alerts-store:
       selbstErledigt). -->
  <button
    type="button"
    class="act melder"
    class:problem={stufe === "critical" || stufe === "problem"}
    class:warnung={stufe === "warning"}
    class:hinweis={stufe === "info"}
    title={melderText}
    aria-label={melderText}
    onclick={() => oeffnePfad("/system/meldungen")}
  >
    <!-- Etwas groesser, sobald etwas offen ist: die Leiste ist sonst
         durchgehend 21px, das Zeichen bricht die Reihe bewusst. -->
    <Icon name="alert-tri" size={stufe ? 24 : 21} />
  </button>
  <!-- Globaler Einstieg; seitenbezogene Einstellungen hat die
       Pfadzeile ueber der Seite (EditorArea).
       Das Zahnrad zeigt auf die EINSTELLUNGEN: dort liegt der
       durchsuchbare Index ueber alles Einstellbare
       (docs/einstellungen-inventar.md) -- samt Anmeldung, Push, Suche
       und Erscheinungsbild. Ein eigenes Konto-Symbol daneben gab es bis
       03.10.2026; es fuehrte auf eine Uebersicht, die nur dieselben
       fuenf Ziele noch einmal zeigte. -->
  <button
    type="button"
    class="act"
    class:aktiv={aufEinstellungen}
    title={t("shell.settings", "Einstellungen")}
    aria-label={t("shell.settings", "Einstellungen")}
    aria-current={aufEinstellungen}
    onclick={() => oeffnePfad("/einstellungen")}
  >
    <Icon name="settings" size={21} />
  </button>
  <!-- Hell/Dunkel in einem Klick, unter dem Zahnrad (Tim 04.10.2026):
       der Griff gehört zu den Einstellungen, nicht zu den Zuständen der
       Anlage -- zuerst stand er in der Statusleiste, das passte nicht.
       Das Symbol zeigt, WOHIN es geht: im Hellen der Mond, im Dunkeln die
       Sonne. Die volle Wahl samt „Auto" bleibt im Einstellungsfenster;
       ein Klick zurück landet wieder bei Auto (lib/theme-wahl.ts), damit
       die schnelle Wahl nicht auf allen Geräten hängen bleibt. -->
  <button type="button" class="act" title={themeTip} aria-label={themeTip} onclick={schalteHellDunkel}>
    <Icon name={dunkel ? "sun" : "moon"} size={21} />
  </button>
</nav>

<style>
  .activity {
    width: 3rem;
    flex: none;
    padding: 0.25rem 0;
    /* Kein eigener Abstand: Leiste und Baum stehen in EINEM Kasten
       (app.css „seiten-rahmen", Tim 18.10.2026). */
  }
  .act {
    position: relative;
    width: 3rem;
    height: 2.75rem;
    display: grid;
    place-items: center;
    color: var(--fg-subtle);
    flex: none;
  }
  .act:hover {
    color: var(--fg);
  }
  /* Im Modus: jedes Bereichssymbol zeigt, dass es sich ziehen lässt
     (gestrichelt in der Bedienfarbe, wie Karten im Anordnen-Modus). */
  .activity.ordnen .act[data-bereich] {
    cursor: grab;
    outline: 1.5px dashed color-mix(in oklab, var(--bedien) 55%, transparent);
    outline-offset: -4px;
    border-radius: 0.6rem;
  }
  .act.aktiv {
    color: var(--fg);
  }
  .act.aktiv::before {
    content: "";
    position: absolute;
    left: 0;
    top: 0.35rem;
    bottom: 0.35rem;
    width: 2px;
    background: var(--marke);
  }
  /* Farbe (app.css „Farbe"): mit „Bereichsfarbe als Ort" trägt der
     gewählte Bereich seine Farbe; mit „Farbige Symbole › Leiste" tun es
     alle, der gewählte auf getöntem Feld. Die Badges darüber behalten
     ihre Stufenfarbe. */
  :global(html[data-farbe-ort="1"]) .act[data-bereich].aktiv,
  :global(html[data-farbe-leiste="1"]) .act[data-bereich] {
    color: var(--bereich);
  }
  :global(html[data-farbe-leiste="1"]) .act[data-bereich].aktiv::after {
    content: "";
    position: absolute;
    inset: 0.3rem 0.45rem;
    border-radius: 0.6rem;
    background: color-mix(in oklab, var(--bereich) 16%, transparent);
  }
  .act[data-bereich] :global(svg) {
    position: relative;
    z-index: 1;
  }
  .trenner {
    width: 1.5rem;
    height: 1px;
    background: var(--border);
    margin: 0.25rem 0;
    flex: none;
  }
  /* Offene Punkte sind ein Hinweis, keine Meldung: gelb statt rot (der
     Grundton der .zaehl-marke), und der Zaehler der Meldungen (unten)
     behaelt seine eigene Farbe. AUSSER es steckt etwas Kaputtes drin
     (summeTon "bad") -- dann wird die Marke rot. Ein Handgriff ist
     gelb, ein Defekt ist rot; sonst uebersieht man drei kritische
     Platten, weil sie aussehen wie zwei faellige Updates (Tim 06.09.). */
  .badge.schwer {
    --stufe: var(--danger);
  }
  /* Das Meldungs-Dreieck faerbt sich nach der dringendsten offenen
     Stufe. NACH :hover/.aktiv notiert, damit die Stufe gewinnt: beide
     haben dieselbe Spezifitaet, es entscheidet die Reihenfolge. Ohne
     offene Meldung bleibt es unauffaellig wie jedes andere Symbol. */
  .melder.problem,
  .melder.problem:hover {
    color: var(--danger);
  }
  .melder.warnung,
  .melder.warnung:hover {
    color: var(--warning);
  }
  /* Hinweis = blau, nicht Akzent. Der Akzent ist in diesem Theme orange
     und liest sich als Warnung -- ein Hinweis warnt aber nicht (Tim
     29.08.: "weder eine Warnung noch ein Fehler aktiv"). Dieselbe
     Lesson wie bei den offenen Markierungen: nie --accent fuer eine
     Einstufung. */
  .melder.hinweis,
  .melder.hinweis:hover {
    color: var(--info);
  }

  /* „Es soll gleich in den Blick kommen" (Tim 28.08.): aus dem Symbol
     wird ein FELD -- getoenter Grund plus Ring in der Stufenfarbe. In
     einer sonst durchgehend grauen Leiste ist das der auffaelligste
     Punkt, und es kommt ohne Bewegung aus: ein Dauerzustand, der
     blinkt, nervt nach der zweiten Minute (der Puls in der
     Statusleiste gehoert zu etwas, das VORBEIGEHT).
     currentColor traegt die Stufenfarbe schon -- eine Regel fuer alle
     drei Stufen statt dreier fast gleicher. */
  .melder.problem::after,
  .melder.warnung::after,
  .melder.hinweis::after {
    content: "";
    position: absolute;
    inset: 0.3rem 0.45rem;
    border-radius: 0.6rem;
    background: color-mix(in oklch, currentColor 15%, transparent);
    box-shadow: inset 0 0 0 1px color-mix(in oklch, currentColor 38%, transparent);
  }
  /* Das Zeichen gehoert VOR den Grund: die getoente Flaeche ist
     positioniert und wuerde sonst darueber liegen. */
  .melder :global(svg) {
    position: relative;
    z-index: 1;
  }
  /* Farbe und Schrift kommen von .zaehl-marke (app.css) -- dieselbe Marke
     wie im Baum. Hier nur Ort und Größe: an der unteren rechten Ecke des
     Symbols, mit einem Ring in der Farbe der Leiste, damit die Striche
     des Symbols nicht in die Ziffer laufen. */
  .lauf-punkt {
    position: absolute;
    z-index: 2;
    top: 0.45rem;
    right: 0.5rem;
    width: 0.5rem;
    height: 0.5rem;
    border-radius: 50%;
    background: var(--accent);
    box-shadow: 0 0 0 2px var(--bg-elev);
  }
  .lauf-punkt.warn {
    background: var(--warning);
  }
  .badge {
    position: absolute;
    /* Über dem Symbol (z-index 1) und über der getönten Kachel des
       gewählten Bereichs. Ohne das lag die Marke seit der Farbe (03.10.)
       UNTER beiden: die Kachel wusch sie aus, die Striche des Symbols
       liefen durch die Ziffer -- daher wirkte sie hier am schwächsten. */
    z-index: 2;
    right: 0.25rem;
    bottom: 0.2rem;
    min-width: 1.25rem;
    height: 1.25rem;
    padding: 0 0.3rem;
    border-radius: 0.65rem;
    font-size: 0.72rem;
    line-height: 1.25rem;
  }
  .badge.zaehl-marke {
    box-shadow:
      inset 0 0 0 1px var(--stufe),
      0 0 0 2px var(--bg);
  }
</style>
